import React, { useEffect } from 'react';
import styled from 'styled-components/macro';
import TipsIcon from 'components/TipsIcon';
import { Checkbox } from '@material-ui/core';
import ImportExportIcon from '@material-ui/icons/ImportExport';
import AscendingIcon from 'assets/images/ascending2.png';
import DescendingIcon from 'assets/images/descending2.png';
import { generateRandomId, produce } from 'utility/general';
import useMeasure from 'hooks/useMeasure';
import { ResizableBox } from 'react-resizable';
import useDialog from 'components/ConfirmDialog/useDialog';
import { useSelector } from 'react-redux';
import { SORTING_ERROR } from 'utility/frontendMessage';

const SLayout = styled.div``;

const SHeaderCellStyle = styled.div`
    border-right: ${(props) => `1px solid ${props.borderColor}`};
    border-bottom: ${(props) => `1px solid ${props.borderColor}`};
    border-top: ${(props) => `1px solid ${props.borderColor}`};
    background: ${(props) => props.background};
    width: 60px;
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    color: ${(props) => props.headerColor};
`;

const SIcon = styled.img`
    width: 15px;
    height: 15px;
`;

const TableHeader = ({
    tableUid,
    isEditAllow,
    isSubTable,
    isExpandRow,
    isCheckboxEnable,
    headerColor,
    borderColor,
    headerBackground,
    columns,
    isDeleteAllow,
    toggleAllCheckbox = () => null,
    isAllCheckboxSelect,
    isSortable,
    textAlign,
    fullWidthTable,
    allowResize,
    getFieldWidth = () => null,
    setColumnWidth = () => null,
    sortParams,
    onTableHeaderWidthChange = (newWidth) => null,
    setSortParams = () => null,
    rows = [],
    disabledCheckbox,
    name = generateRandomId(),
    showGroupTab = false,
    isRowNumberEnabled = false,
    indexHeaderName = "",
}) => {
    const tableCurrentHeight = useSelector((state) => state.table[`table-${tableUid}-scrollHeight`] ?? 0);
    // const tableCurrentHeight = 0;
    const [uid, { height }] = useMeasure();
    const [headerUid, { width }] = useMeasure();
    const showDialog = useDialog();

    useEffect(() => {
        onTableHeaderWidthChange(width);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [width]);

    const onColumnHeightUpdate = (e, data, field) => {
        const newWidth = data.size.width;
        setColumnWidth(
            produce((draft) => {
                const index = draft.findIndex((item) => item.field === field);
                draft[index].width = newWidth;
            }),
        );
    };

    const onSortLabelClick = async (row) => {
        const alternativeSortKey = row.sortKey;
        const key = alternativeSortKey ? alternativeSortKey : row.field;
        const isExist = sortParams.key === key;
        const currentColumn = columns.find((item) => item.field === row.field);

        // custom sorting logic (backend sort)
        if (currentColumn.onSort) {
            try {
                if (!isExist) {
                    await currentColumn.onSort(key, true);
                    setSortParams({ key: key, isDesc: true, isServer: true });
                } else {
                    await currentColumn.onSort(key, !sortParams.isDesc);
                    setSortParams(
                        produce((draft) => {
                            draft.isDesc = !sortParams.isDesc;
                            draft.isServer = true;
                        }),
                    );
                }
            } catch (error) {
                showDialog({
                    description: SORTING_ERROR,
                    disableCancelButton: true,
                });

                throw error;
            }
        } else {
            // sorting in frontend only
            if (!isExist) {
                setSortParams({ key: key, isDesc: true, isServer: false });
            } else {
                setSortParams(
                    produce((draft) => {
                        draft.isDesc = !sortParams.isDesc;
                        draft.isServer = false;
                    }),
                );
            }
        }
    };

    const getSortArrow = (row) => {
        const { key, isDesc } = sortParams;

        const sortKey = row.sortKey ? row.sortKey : row.field;

        if (key !== sortKey) {
            return (
                <div style={{ marginLeft: 5, paddingTop: 5, cursor: 'pointer' }} onClick={() => onSortLabelClick(row)}>
                    <ImportExportIcon fontSize={'small'} style={{ color: '#a9a9a9' }} />
                </div>
            );
        } else {
            return (
                <div
                    style={{ marginLeft: 5, cursor: 'pointer', paddingTop: 5, width: 20, height: 20 }}
                    onClick={() => onSortLabelClick(row)}
                >
                    {!isDesc ? <SIcon src={AscendingIcon} /> : <SIcon src={DescendingIcon} />}
                </div>
            );
        }
    };

    const extraProps = {};

    if (fullWidthTable) {
        extraProps.width = '100%';
    }

    return (
        <SLayout style={{ height }}>
            <div
                className={uid}
                style={{
                    display: 'flex',
                    fontSize: 12,
                    fontWeight: 'bold',
                    borderLeft: `1px solid ${borderColor}`,
                    position: 'absolute',
                    left: 0,
                    top: tableCurrentHeight,
                    right: 0,
                    zIndex: isSubTable ? 1 : 1000,
                    minHeight: 50, // minimum height to prevent sort icon overlap with resize indicator
                    opacity: 1,
                    paddingTop: showGroupTab ? 30 : 0,
                }}
            >
                <div className={headerUid} style={{ display: 'flex', ...extraProps }}>
                    {isRowNumberEnabled && (
                        <SHeaderCellStyle
                            borderColor={borderColor}
                            headerColor={headerColor}
                            background={headerBackground}
                        >
                            {indexHeaderName}
                        </SHeaderCellStyle>
                    )}
                    {isExpandRow && (
                        <SHeaderCellStyle
                            borderColor={borderColor}
                            headerColor={headerColor}
                            background={rows.length <= 0 ? headerBackground : '#fff'}
                        />
                    )}
                    {isCheckboxEnable && (
                        <SHeaderCellStyle
                            id={`${name}_checkbox`}
                            borderColor={borderColor}
                            headerColor={headerColor}
                            background={headerBackground}
                        >
                            <Checkbox
                                color="primary"
                                checked={isAllCheckboxSelect}
                                onChange={toggleAllCheckbox}
                                disabled={disabledCheckbox}
                            />
                        </SHeaderCellStyle>
                    )}
                    {isEditAllow && (
                        <SHeaderCellStyle
                            id={`${name}_edit`}
                            borderColor={borderColor}
                            headerColor={headerColor}
                            background={headerBackground}
                        >
                            Edit
                        </SHeaderCellStyle>
                    )}

                    {columns
                        .filter((item) => !item.hiddenInTableColumn)
                        .map((item, index) => {
                            const content = (
                                <div
                                    style={{
                                        padding: isSortable ? '0 22px' : '0 10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <div>
                                        {item.headerName} {item.tooltip && <TipsIcon title={item.tooltip} />}
                                    </div>

                                    {isSortable && !item.disableItemSort && (
                                        <div style={{ position: 'absolute', right: 2, fontSize: 14 }}>
                                            {getSortArrow(item)}
                                        </div>
                                    )}
                                </div>
                            );

                            if (item.fixWidth) {
                                return (
                                    <SHeaderCellStyle
                                        key={item.field}
                                        borderColor={borderColor}
                                        headerColor={headerColor}
                                        background={headerBackground}
                                        style={{
                                            position: 'relative',
                                            flexShrink: 0,
                                            width: getFieldWidth(item.field),
                                        }}
                                    >
                                        {content}
                                    </SHeaderCellStyle>
                                );
                            }

                            return (
                                <ResizableBox
                                    id={`${name}_${item.field}`}
                                    key={item.field}
                                    onResizeStop={(e, data) => onColumnHeightUpdate(e, data, item.field)}
                                    style={{
                                        position: 'relative',
                                        borderRight: `1px solid ${borderColor}`,
                                        borderBottom: `1px solid ${borderColor}`,
                                        borderTop: `1px solid ${borderColor}`,
                                        background: headerBackground,
                                        color: headerColor,
                                        flexShrink: 0,
                                        overflow: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: textAlign === 'left' ? 'flex-start' : 'center',
                                        textAlign: textAlign ? textAlign : 'center',
                                        lineHeight: 1.5,
                                        padding: '5px 0',
                                        flexGrow: fullWidthTable ? 1 : 0,
                                    }}
                                    minConstraints={[80, -1]}
                                    width={getFieldWidth(item.field)}
                                    height={-1}
                                    resizeHandles={allowResize ? ['e'] : []}
                                >
                                    {content}
                                </ResizableBox>
                            );
                        })}
                    {isDeleteAllow && (
                        <SHeaderCellStyle
                            borderColor={borderColor}
                            headerColor={headerColor}
                            background={headerBackground}
                        >
                            Remove
                        </SHeaderCellStyle>
                    )}
                </div>
            </div>
        </SLayout>
    );
};

export default TableHeader;
